import { useCallback } from 'react'
import { compose } from 'redux'

import { getDisplayValues } from '../components/selectToggle/helpers/getDisplayValues'
import { deDupeTreeItems } from '../helpers/deDupeTreeItems'
import { getAllAncestorsForItem } from '../helpers/getAllAncestorsForItem'
import { getAllDescendantsForItem } from '../helpers/getAllDescendantsForItem'
import { getAncestorsToRemove } from '../helpers/getAncestorsToRemove'
import { getLeaves } from '../helpers/getLeaves'
import { getOriginalData } from '../helpers/getOriginalData'
import { getTreeItemState } from '../helpers/getTreeItemState'
import { IDataShape } from '../models/dataShape'
import { IUseTreeDataResult } from '../models/iUseTreeData'
import { IUseTreeSelectParameters, IUseTreeSelectResult } from '../models/iUseTreeSelect'
import { ITreeItemClickFn } from '../models/treeSelectProps'
import { ITreeStructure } from '../models/treeStructure'

import { useTreeData } from './useTreeData'

export const useTreeSelect = <T extends IDataShape>({
  data,
  values,
  itemCaption,
  onChange,
  onSearch,
  typeOfSelectedNodes,
}: IUseTreeSelectParameters<T>): IUseTreeSelectResult & IUseTreeDataResult => {
  /** -------------------- Variables ----------------------- */
  const {
    treeData,
    flatData,
    searchValue,
    setSearchValue,
    showOnlySelected,
    setShowOnlySelected,
    selectedValues,
  } = useTreeData({
    data,
    values,
    itemCaption,
    onSearch,
    typeOfSelectedNodes,
  })

  /** -------------------- Methods ----------------------- */
  const handleChangeEvent = useCallback(
    (allSelectedItems: ITreeStructure[]) => {
      const displayedOriginalItems = compose(getOriginalData<T>, getDisplayValues)(allSelectedItems)
      const onlyLeavesItems = compose(getOriginalData<T>, getLeaves)(allSelectedItems)
      const allSelectedOriginalItems = getOriginalData<T>(allSelectedItems)
      onChange(displayedOriginalItems, allSelectedOriginalItems, onlyLeavesItems)
    },
    [onChange]
  )

  const removeAll = useCallback(() => {
    handleChangeEvent([])
  }, [handleChangeEvent])

  const removeItem = useCallback<ITreeItemClickFn>(
    (itemId) => {
      const item = flatData.find((it) => it.itemId === itemId)
      if (!item) {
        return
      }

      const allAncestors = getAllAncestorsForItem(item)
      const allDescendants = getAllDescendantsForItem(item).filter(
        (it) => it.itemId !== item.itemId
      )

      if (item.parentId === null) {
        const itemsToUncheck = [...allAncestors, item, ...allDescendants]
        const newState = selectedValues
          .filter((it) => !itemsToUncheck.some((uncheck) => uncheck.itemId === it.itemId))
          .map((it, i, origin) => ({
            ...it,
            checkedState: getTreeItemState(it.itemId, flatData, origin),
          }))
        handleChangeEvent(newState)

        return
      }

      let updatedState = selectedValues.filter((it) => item.itemId !== it.itemId)
      if (allDescendants.length) {
        updatedState = updatedState.filter((it) => {
          return !allDescendants.some((descendant) => descendant.itemId === it.itemId)
        })
      }

      const ancestorsToRemove = getAncestorsToRemove(item, updatedState)
      updatedState = updatedState
        .filter((it) => !ancestorsToRemove.some((ancestor) => ancestor.itemId === it.itemId))
        .map((it, i, origin) => ({
          ...it,
          checkedState: getTreeItemState(it.itemId, flatData, origin),
        }))

      handleChangeEvent(updatedState)
    },
    [selectedValues, handleChangeEvent, flatData]
  )

  const selectItem = useCallback<ITreeItemClickFn>(
    (itemId) => {
      const item = flatData.find((it) => it.itemId === itemId)
      if (!item) {
        return
      }
      const allAncestors = getAllAncestorsForItem(item)
      const allDescendants = getAllDescendantsForItem(item).filter(
        (it) => it.itemId !== item.itemId
      )

      const newState = [...selectedValues, ...allAncestors, item, ...allDescendants]
        .filter(deDupeTreeItems)
        .map((it, i, origin) => ({
          ...it,
          checkedState: getTreeItemState(it.itemId, flatData, origin),
        }))

      handleChangeEvent(newState)
    },
    [selectedValues, handleChangeEvent, flatData]
  )

  return {
    removeItem,
    removeAll,
    selectItem,

    treeData,
    flatData,
    searchValue,
    setSearchValue,
    showOnlySelected,
    setShowOnlySelected,
    selectedValues,
  }
}
